import { Navigate, useLocation } from "react-router-dom";

import _ from "lodash";

import PageLayout from "commons/components/layout/PageLayout";

import { RedirectMapContextProvider } from "commons/context/redirectMap";
import { RouteContextProvider } from "commons/context/route";

import { RouteType } from "commons/types/route";

import { makeUrl } from "commons/utils/http";
import { check, checkUserAuthorities } from "commons/utils/utils";

export type LoginUrlQueryParamsType = {
    from?: string;
};

type MakeLoginUrlInterface = (queryParams: LoginUrlQueryParamsType) => string;

type PropsType = {
    readonly route: RouteType;
    readonly userAuthorities: Array<string>;
    readonly accessToken: string;
    readonly defaultTitle: string;
    readonly useDynamicTitle?: boolean;
    readonly useNavigateBack?: boolean;
    readonly footerPageContent?: JSX.Element;
    readonly cantViewRouteUrl?: string;
    readonly maxPathWayElements?: number;
    readonly makeLoginUrl?: MakeLoginUrlInterface;
};

const makeLoginUrlBase = (queryParams: LoginUrlQueryParamsType): string => {
    return makeUrl("/auth/login", undefined, queryParams);
};

function PrivateRouteBase({
    route,
    userAuthorities,
    accessToken,
    defaultTitle,
    useDynamicTitle,
    useNavigateBack,
    footerPageContent,
    cantViewRouteUrl,
    maxPathWayElements,
    makeLoginUrl,
}: PropsType) {
    const location = useLocation();

    const isLoggedIn = check(accessToken);

    const canViewRoute = checkUserAuthorities(
        userAuthorities,
        route.neededAuthorities,
        route.oneOf
    );

    if (isLoggedIn && canViewRoute) {
        // The page gets reloaded, even if I'm already at same path
        return (
            <RouteContextProvider route={route}>
                <RedirectMapContextProvider userAuthorities={userAuthorities}>
                    <PageLayout
                        route={route}
                        defaultTitle={defaultTitle}
                        useDynamicTitle={useDynamicTitle}
                        useNavigateBack={useNavigateBack}
                        footerPageContent={footerPageContent}
                        key={_.uniqueId()}
                        maxPathWayElements={maxPathWayElements}
                    />
                </RedirectMapContextProvider>
            </RouteContextProvider>
        );
    }

    if (isLoggedIn && !canViewRoute && cantViewRouteUrl) {
        return <Navigate to={cantViewRouteUrl} replace />;
    }

    const queryParams: LoginUrlQueryParamsType = {};

    if (!isLoggedIn) {
        queryParams.from = location.pathname;
    }

    let to: string = "";

    if (makeLoginUrl !== undefined) {
        to = makeLoginUrl(queryParams);
    } else {
        to = makeLoginUrlBase(queryParams);
    }

    return <Navigate to={to} replace />;
}

export default PrivateRouteBase;
